import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@/lib/router-compat";
import { Pause, Play } from "lucide-react";
import { format } from "date-fns";
import SmartImage from "@/components/ui/SmartImage";
import { getEpisodesForWeek } from "@/services/podcastService";
import { usePodcastPlayer } from "@/context/PodcastPlayerContext";
import type { PodcastEpisode } from "@/types/podcast";
import { SectionLabel } from "./SectionLabel";
import { T } from "@/i18n/T";

function formatDuration(seconds?: number) {
  if (!seconds) return "";
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
}

export function WeeklyListen({ weekStart, weekEnd }: { weekStart: string; weekEnd: string }) {
  const navigate = useNavigate();
  const { playEpisode, pause, isPlaying, currentEpisode } = usePodcastPlayer();

  const { data: episodes = [] } = useQuery({
    queryKey: ["weekly-listen", weekStart, weekEnd],
    queryFn: () => getEpisodesForWeek(weekStart, weekEnd, 6),
    enabled: !!weekStart && !!weekEnd,
    staleTime: 10 * 60 * 1000,
  });

  if (!episodes.length) return null;

  const toggle = (episode: PodcastEpisode) => {
    if (currentEpisode?.id === episode.id && isPlaying) pause();
    else playEpisode(episode);
  };

  return (
    <section className="mb-16">
      <SectionLabel><T k="auto.listen-this-week-dda7" /></SectionLabel>
      <ul className="grid gap-3 md:grid-cols-2">
        {episodes.map((episode) => {
          const active = currentEpisode?.id === episode.id && isPlaying;
          return (
            <li
              key={episode.id}
              className="flex items-center gap-4 rounded-2xl border border-border bg-card p-4"
            >
              <div className="relative h-16 w-16 flex-shrink-0 overflow-hidden rounded-xl bg-muted">
                {episode.image_url ? (
                  <SmartImage
                    src={episode.image_url}
                    alt={episode.title}
                    w={64}
                    h={64}
                    className="h-full w-full object-cover"
                  />
                ) : null}
              </div>

              <div className="min-w-0 flex-1">
                <button
                  type="button"
                  onClick={() =>
                    navigate(`/podcasts/${episode.podcast_id}/episodes/${episode.id}`)
                  }
                  className="block w-full truncate text-left font-display font-semibold text-foreground hover:underline"
                >
                  {episode.title}
                </button>
                <p className="mt-1 truncate font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  {episode.podcast?.name}
                  {episode.published_at
                    ? ` · ${format(new Date(episode.published_at), "MMM d")}`
                    : ""}
                  {episode.duration_seconds
                    ? ` · ${formatDuration(episode.duration_seconds)}`
                    : ""}
                </p>
              </div>

              <button
                type="button"
                onClick={() => toggle(episode)}
                aria-label={active ? "Pause episode" : "Play episode"}
                className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground transition-opacity hover:opacity-90"
              >
                {active ? (
                  <Pause className="h-4 w-4" />
                ) : (
                  <Play className="h-4 w-4 translate-x-[1px]" />
                )}
              </button>
            </li>
          );
        })}
      </ul>

      <Link
        to="/podcasts"
        className="mt-5 inline-block font-mono text-[11px] uppercase tracking-[0.22em] text-primary hover:underline"
      >
        <T k="auto.all-podcasts-d5fb" />
      </Link>
    </section>
  );
}
